import React from 'react';
import { connect } from 'react-redux';

import InfluencerModalActions from 'modules/influencers/components/modals/modal-actions/InfluencerModalActions';
import Page from 'components/page/Page';
import InfluencerComposedMessage from 'components/influencer-composed-message/InfluencerComposedMessage';
import InfluencerProfile from '../components/InfluencerProfile';

import { fetchOrganizationSettings } from 'modules/organizations/reducers/organizationSettings';
import { forceProfileUpdate } from 'modules/admin/reducers/admin-profiles';

import { t } from 'utils/language';
import { canUserEditInfluencerProfile } from 'utils/accessRules';
import { isAdmin } from 'utils/account';
import { isBrandsFocusedOrganization } from 'utils/organization';

import type {
  Account,
  InfluencerTile,
  Organization,
  Role
} from 'types';

interface StateProps {
  params: {
    id: unknown
  },
  profile: InfluencerTile,
  currentOrganization: Organization,
  currentAccount: Account,
  roles: Array<Role>
}

interface DispatchProps {
  dispatchFetchOrganizationSettings: (a: number) => Promise<void>,
  dispatchForceProfileUpdate: (profileId: number) => void
}

type Props = StateProps & DispatchProps;

const InfluencerProfilePage = ({
  params,
  profile,
  currentOrganization,
  currentAccount,
  roles,
  dispatchForceProfileUpdate,
  dispatchFetchOrganizationSettings
}: Props) => {
  const actionComponent: Array<JSX.Element> = [];
  const isProfileLoaded = Boolean(profile && profile.id);
  const canEdit = canUserEditInfluencerProfile(profile, currentOrganization, currentAccount, roles);
  const isOwner = (currentOrganization.influencerProfiles || [])[0]
    && (currentOrganization.influencerProfiles || [])[0].id === profile.id;
  const isBrand = isBrandsFocusedOrganization(currentOrganization);

  const profileId = profile?.id;
  React.useEffect(() => {
    if (profileId && isBrand) {
      dispatchForceProfileUpdate(profileId);
    }
  }, [profileId, isBrand]);

  React.useEffect(() => {
    if (!currentOrganization?.id) {
      return;
    }

    dispatchFetchOrganizationSettings(currentOrganization.id);
  }, []);

  const canHaveModalActions = (isProfileLoaded && !canEdit) || isAdmin(currentAccount, roles);

  if (isBrand && canHaveModalActions) {
    actionComponent.push(
      <InfluencerModalActions
        key="profile-modal-buttons"
        influencer={profile}
        isFreemium={currentOrganization.isFreemium || false}
      />
    );
  }

  return (
    <Page
      documentTitle={`${t('influencer_profile_title', { influencerName: isProfileLoaded ? `${profile.name} - ` : '' })}`}
    >
      {isOwner && (
        <InfluencerComposedMessage />
      )}
      <InfluencerProfile
        key={`influencer-profile-${params.id}`} // rebuild the whole page if id changes
        profileId={Number(params.id)}
        actionComponent={actionComponent}
        canEdit={canEdit}
        activeTab={window.location.hash}
      />
    </Page>
  );
};

const mapStateToProps = ({ influencerProfile, organizations, account }) => ({
  profile: influencerProfile.profile,
  currentOrganization: organizations.currentOrganization ?? {},
  currentAccount: account.currentAccount,
  roles: account.roles
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchOrganizationSettings: (
    organizationId
  ) => dispatch(fetchOrganizationSettings(organizationId)),
  dispatchForceProfileUpdate: (profileId: number) => dispatch(forceProfileUpdate(profileId))
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerProfilePage);
